import { parseDice } from './dice.ts';
import { asFile, asNumber, asRange, asString, asStringArray, formulaValue, numericValue, selectOption, valueToText, voteScore } from './fields.ts';
import type { CollectionData, FieldDef, RecordData } from './model.ts';
import type { ProjectDoc } from './project-doc.ts';

/**
 * Data exports meant for game engines: a collection becomes an array of plain objects that a
 * Unity/Unreal/Godot importer can consume directly, or a CSV for spreadsheets.
 */

export type KeyStyle = 'name' | 'camelCase' | 'snake_case';

export function toKey(name: string, style: KeyStyle): string {
  if (style === 'name') return name;
  const words = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return style === 'camelCase' ? 'field' : 'field';
  if (style === 'snake_case') return words.map((w) => w.toLowerCase()).join('_');
  return words.map((w, i) => (i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1).toLowerCase())).join('');
}

export interface ExportOptions {
  keyStyle?: KeyStyle;
  /** Relations as record titles (readable) or ids (stable). */
  relations?: 'titles' | 'ids';
  includeIds?: boolean;
}

function exportValue(p: ProjectDoc, record: RecordData, field: FieldDef, collection: CollectionData, opts: Required<ExportOptions>): unknown {
  const v = record.values[field.id];
  const ctx = { ...p.valueContext(), fields: collection.fields };
  switch (field.type) {
    case 'number':
    case 'rating':
      return asNumber(v);
    case 'checkbox':
      return v === true;
    case 'range': {
      const [min, max] = asRange(v);
      return min === null && max === null ? null : { min, max };
    }
    case 'dice': {
      const s = asString(v);
      const d = parseDice(s);
      return s ? { notation: s, min: d?.min ?? null, avg: d?.avg ?? null, max: d?.max ?? null } : null;
    }
    case 'formula':
      return formulaValue(record, field, ctx);
    case 'votes':
      return voteScore(record);
    case 'select':
      return selectOption(field, v)?.label ?? null;
    case 'multiSelect':
      return asStringArray(v).map((id) => selectOption(field, id)?.label).filter(Boolean);
    case 'person':
      return asStringArray(v).map((id) => p.getPerson(id)?.name ?? id);
    case 'relation':
      return asStringArray(v)
        .filter((id) => p.getRecord(id))
        .map((id) => (opts.relations === 'ids' ? id : p.getRecord(id)!.title));
    case 'file': {
      const f = asFile(v);
      return f ? { name: f.name, mime: f.mime, size: f.size, hash: f.hash } : null;
    }
    case 'date':
    case 'text':
    case 'url':
    case 'color':
      return asString(v) || null;
  }
}

export function exportCollectionJSON(p: ProjectDoc, collectionId: string, options: ExportOptions = {}, records?: readonly RecordData[]): Record<string, unknown>[] {
  const opts: Required<ExportOptions> = { keyStyle: options.keyStyle ?? 'camelCase', relations: options.relations ?? 'titles', includeIds: options.includeIds ?? true };
  const c = p.getCollection(collectionId);
  if (!c) return [];
  const rows = records ?? p.listRecords(collectionId);
  const titleKey = toKey(c.titleLabel || 'Name', opts.keyStyle);
  return rows.map((r) => {
    const out: Record<string, unknown> = {};
    if (opts.includeIds) out.id = r.id;
    out[titleKey] = r.title;
    for (const f of c.fields) out[toKey(f.name, opts.keyStyle)] = exportValue(p, r, f, c, opts);
    return out;
  });
}

const csvCell = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

export function exportCollectionCSV(p: ProjectDoc, collectionId: string, records?: readonly RecordData[]): string {
  const c = p.getCollection(collectionId);
  if (!c) return '';
  const rows = records ?? p.listRecords(collectionId);
  const ctx = { ...p.valueContext(), fields: c.fields };
  const header = [c.titleLabel || 'Name', ...c.fields.map((f) => f.name)];
  const lines = [header.map(csvCell).join(',')];
  for (const r of rows) {
    const cells = [r.title, ...c.fields.map((f) => (f.type === 'number' || f.type === 'formula' ? String(numericValue(r, f, ctx) ?? '') : valueToText(r, f, ctx)))];
    lines.push(cells.map(csvCell).join(','));
  }
  return lines.join('\r\n');
}
